/* eslint-disable max-len */
import { store } from 'store/index';
import Moment from 'moment';
import { periodosMap } from 'views/Tramites/GPM/enums';

export const sortCargos = (_cargos = []) => {
  const { ordenar_cargos_acendente = false } = store.getState().auth.entidad;
  const sortIdentificadores = (
    { tipo_de_cargo: { identificador_periodo: a } },
    { tipo_de_cargo: { identificador_periodo: b } },
  ) => (ordenar_cargos_acendente ? a - b : b - a);

  const sortPeriodos = (a, b) => (ordenar_cargos_acendente ? a - b : b - a);
  const getAccesories = (cargo_padre) => _cargos
    .filter((e) => e.cargo_padre === cargo_padre && e.es_accesorio);

  const cargosNoAccesorios = _cargos
    .filter((c) => !c.es_accesorio)
    .sort((a, b) => (ordenar_cargos_acendente ? a.id - b.id : b.id - a.id));

  const periodos = [...new Set(cargosNoAccesorios
    .map((cargo) => cargo.tipo_de_cargo.periodo_fiscal.id))].sort(sortPeriodos);
  const entries = periodos
    .map((p) => [p, cargosNoAccesorios.filter((c) => c.tipo_de_cargo.periodo_fiscal.id === p)
      .sort(sortIdentificadores)]);
  const cargosNoAccesoriosGroupedByPeriod = Object.fromEntries(entries);
  const output = periodos.map((p) => cargosNoAccesoriosGroupedByPeriod[p]).flat()
    .reduce((a, b) => [...a, b, ...getAccesories(b.id)], []);
  return output;
};

const quantityOfCargosByPeriodo = Object.freeze({
  [periodosMap.DIARIO]: 365,
  [periodosMap.SEMANAL]: 52,
  [periodosMap.QUINCENAL]: 26,
  [periodosMap.MENSUAL]: 12,
  [periodosMap.BIMESTRAL]: 6,
  [periodosMap.TRIMESTRAL]: 4,
  [periodosMap.CUATRIMESTRAL]: 3,
  [periodosMap.SEMESTRAL]: 2,
  [periodosMap.ANUAL]: 1,
});

const obtenerClaveAgrupacion = (c) => {
  const periodoFiscal = c.tipo_de_cargo.periodo_fiscal.periodo;
  const { periodo } = c.tipo_de_cargo;
  const sistema = c.tipo_de_cargo.sistemas?.id || c.tipo_de_cargo.sistemas;
  return `${periodoFiscal}__${sistema}__${periodo}`;
};

const agruparCargosPorPeriodos = (cargos) => cargos
  .filter((c) => !c.es_accesorio)
  .map((c) => [obtenerClaveAgrupacion(c), c.id])
  .reduce((acum, [key, value]) => {
    const newAcum = { ...acum };
    if (!acum[key]) newAcum[key] = [];
    newAcum[key].push(value);
    return newAcum;
  }, {});

const actualizarDescuentoPorPeriodo = (cargos, selectedRowKeys) => {
  const cargosAgrupadosPorPeriodos = agruparCargosPorPeriodos(cargos);

  return cargos.map((c) => {
    const key = obtenerClaveAgrupacion(c);
    const quantity = quantityOfCargosByPeriodo[c.tipo_de_cargo.periodo.toString()];
    const cargosPeriodo = cargosAgrupadosPorPeriodos[key] || [];

    const periodoCompletoSeleccionado = cargosPeriodo.length === quantity
      && cargosPeriodo.every((id) => selectedRowKeys.includes(id));

    return {
      ...c,
      aplicaDescuentoPorPeriodo: periodoCompletoSeleccionado,
      periodoCompletoSeleccionado,
      cargosPeriodo,
    };
  });
};

const filterPorPeriodo = (d) => d.aplica_por_periodo;
const filterNoPorPeriodo = (d) => !d.aplica_por_periodo;
const filterMultas = (d) => d.es_multa;
const filterNoMultas = (d) => !d.es_multa;
const reduceImporteTotal = (arr, key = 'importe_total') => arr.reduce((accum, curr) => accum + curr[key], 0);

const makeDescuentosNormalizer = (
  solo_aplica_el_descuento_mayor,
  aplicaDescuentoPorPeriodo,
  keyDescuentos = 'descuentos',
) => (item) => {
  let _descuentos = [...item[keyDescuentos]]
    .filter((d) => d.estados_globales !== 5 && !d.cancelado && !d.saldado);
  if (keyDescuentos === 'descuentos_especiales') {
    _descuentos = _descuentos.filter((d) => d.estados_globales === 4);
  }
  // descuentos siempre aplicables
  let descuentosAplicados = _descuentos.filter(filterNoPorPeriodo);
  if (aplicaDescuentoPorPeriodo) {
    // descuentos aplicables por periodo
    const descuentosPorPeriodo = _descuentos.filter(filterPorPeriodo);
    descuentosAplicados = [...descuentosAplicados, ...descuentosPorPeriodo];
  }
  // mantener descuento mayor si configuración lo especifica
  if (descuentosAplicados.length && solo_aplica_el_descuento_mayor) {
    [descuentosAplicados] = descuentosAplicados.sort((a, b) => b.importe_total - a.importe_total);
    descuentosAplicados = [descuentosAplicados];
  }
  descuentosAplicados = descuentosAplicados.map((d) => d.id);
  // para descuentos_aplicables y descuentos_especiales de cargo
  const descuentos = _descuentos.map((d) => ({ ...d, aplicado: descuentosAplicados.includes(d.id) }));
  return { ...item, [keyDescuentos]: descuentos };
};

const fechaActualEnRango = (config) => {
  const { fecha_inicial, fecha_final } = config;
  if (!fecha_inicial || !fecha_final) return true;
  const hoy = Moment();
  return hoy.isSameOrAfter(Moment(fecha_inicial), 'day')
    && hoy.isSameOrBefore(Moment(fecha_final), 'day');
};

/**
 * Crea una función que omite los recargos de un cargo cuando se cumplen las condiciones
 * de cancelación configuradas para su periodo fiscal.
 *
 * La omisión solo aplica cuando el cargo pertenece a un periodo fiscal **completo**
 * seleccionado (`periodoCompletoSeleccionado === true`) y existe configuración para
 * su periodo fiscal en `configCancelacionesRecargos`.
 *
 * @param {boolean} periodoCompletoSeleccionado - Indica si todos los cargos del periodo fiscal
 *   están seleccionados. Si es `false`, ningún recargo se omite.
 * @param {Array<{
 *   periodo: string,
 *   tipos_de_recargo: Array<{ id: number|string }>
 * }>} configCancelacionesRecargos - Lista de configuraciones de cancelación. Cada entrada
 *   corresponde a un periodo fiscal e indica qué tipos de recargo deben omitirse.
 * @returns {function(cargo: object): object} Función que recibe un cargo y devuelve
 *   una copia con una propiedad modificada:
 *   - `recargos` — solo los recargos que **no** deben omitirse.
 */
const crearOmisionDeRecargos = (
  periodoCompletoSeleccionado,
  configCancelacionesRecargos,
) => (cargo) => {
  const periodoFiscal = cargo?.tipo_de_cargo?.periodo_fiscal?.periodo;
  const configsDelPeriodo = configCancelacionesRecargos
    .filter((c) => c.periodo === periodoFiscal)
    .filter(fechaActualEnRango);
  const sinCondicionesDeOmision = !configsDelPeriodo.length
    || !periodoCompletoSeleccionado
    || !cargo?.recargos?.length;

  if (sinCondicionesDeOmision) {
    return {
      ...cargo,
      recargos: cargo?.recargos.map((r) => ({ ...r, omitir: false })),
    };
  }

  const tiposAOmitir = new Set(configsDelPeriodo
    .flatMap((c) => c.tipos_de_recargo.map((t) => t.id)));
  const recargosConOmision = cargo
    .recargos.map((r) => ({ ...r, omitir: tiposAOmitir.has(r.tipo_de_recargo.id) }));

  return {
    ...cargo,
    recargos: recargosConOmision.filter((r) => !r.omitir),
  };
};

/**
 * Crea una función que omite las actualizaciones de un cargo cuando se cumplen las condiciones
 * de cancelación configuradas para su periodo fiscal.
 *
 * Funciona de forma análoga a `crearOmisionDeRecargos`, pero opera sobre el array
 * `actualizaciones` del cargo y consulta `tipos_de_actualizaciones` en la configuración.
 *
 * La omisión solo aplica cuando el cargo pertenece a un periodo fiscal **completo**
 * seleccionado (`periodoCompletoSeleccionado === true`) y existe configuración para
 * su periodo fiscal en `configCancelacionesActualizaciones`.
 *
 * @param {boolean} periodoCompletoSeleccionado - Indica si todos los cargos del periodo fiscal
 *   están seleccionados. Si es `false`, ninguna actualización se omite.
 * @param {Array<{
 *   periodo: string,
 *   tipos_de_actualizaciones: Array<{ id: number|string }>
 * }>} configCancelacionesActualizaciones - Lista de configuraciones de cancelación. Cada entrada
 *   corresponde a un periodo fiscal e indica qué tipos de actualización deben omitirse.
 * @returns {function(cargo: object): object} Función que recibe un cargo y devuelve
 *   una copia con una propiedad modificada:
 *   - `actualizaciones` — solo las actualizaciones que **no** deben omitirse.
 */
const crearOmisionDeActualizaciones = (
  periodoCompletoSeleccionado,
  configCancelacionesActualizaciones,
) => (cargo) => {
  const periodoFiscal = cargo?.tipo_de_cargo?.periodo_fiscal?.periodo;
  const configsDelPeriodo = configCancelacionesActualizaciones
    .filter((c) => c.periodo === periodoFiscal)
    .filter(fechaActualEnRango);
  const sinCondicionesDeOmision = !configsDelPeriodo.length
    || !periodoCompletoSeleccionado
    || !cargo?.actualizaciones?.length;

  if (sinCondicionesDeOmision) {
    return {
      ...cargo,
      actualizaciones: cargo?.actualizaciones.map((a) => ({ ...a, omitir: false })),
    };
  }

  const tiposAOmitir = new Set(configsDelPeriodo
    .flatMap((c) => c.tipos_de_actualizaciones.map((t) => t.id)));
  const actualizacionesConOmision = cargo.actualizaciones
    .map((a) => ({ ...a, omitir: tiposAOmitir.has(a.tipo_de_actualizacion_id) }));

  return {
    ...cargo,
    actualizaciones: actualizacionesConOmision.filter((a) => !a.omitir),
  };
};

const filterAplicado = (d) => d.aplicado;
const filterDescuentosAplicados = (arr) => arr.map((e) => e.descuentos.filter(filterAplicado)).flat();

export const recalculateCargos = (cargos = [], _selectedRowKeys, configCancelacionesRecargos = []) => {
  const selectedRowKeys = Array.isArray(_selectedRowKeys) ? _selectedRowKeys
    : Object.keys(_selectedRowKeys).reduce((acum, curr) => acum.concat(_selectedRowKeys[curr]), []);

  const _cargos = actualizarDescuentoPorPeriodo(cargos, selectedRowKeys, configCancelacionesRecargos)
    .map((cargo) => {
      const {
        aplicaDescuentoPorPeriodo,
        periodoCompletoSeleccionado,
        tipo_de_cargo: { solo_aplica_el_descuento_mayor },
      } = cargo;
      const defDescuentosNormalizer = makeDescuentosNormalizer(solo_aplica_el_descuento_mayor, aplicaDescuentoPorPeriodo);
      const descuentosAplicablesNormalizer = makeDescuentosNormalizer(solo_aplica_el_descuento_mayor, aplicaDescuentoPorPeriodo, 'descuentos_aplicables');
      const descuentosEspecialesNormalizer = makeDescuentosNormalizer(solo_aplica_el_descuento_mayor, aplicaDescuentoPorPeriodo, 'descuentos_especiales');

      const [{ descuentos_aplicables }] = [cargo].map(descuentosAplicablesNormalizer);
      const [{ descuentos_especiales }] = [cargo].map(descuentosEspecialesNormalizer);

      // recargos
      const omitirRecargos = crearOmisionDeRecargos(periodoCompletoSeleccionado, configCancelacionesRecargos);
      const [{ recargos: rawRecargos }] = [cargo].map(omitirRecargos);
      const recargos = rawRecargos.filter(filterNoMultas).map(defDescuentosNormalizer);
      const recargosMulta = rawRecargos.filter(filterMultas).map(defDescuentosNormalizer);
      const recargos_base = reduceImporteTotal(recargos);

      // descuentos de recargos
      const descuentos_de_recargos = reduceImporteTotal(filterDescuentosAplicados(recargos));
      const descuentos_de_recargos_multas = reduceImporteTotal(filterDescuentosAplicados(recargosMulta));

      // gastos
      const gastos = cargo.gastos.filter(filterNoMultas).map(defDescuentosNormalizer);
      const gastosMulta = cargo.gastos.filter(filterMultas).map(defDescuentosNormalizer);
      const gastos_totales = reduceImporteTotal(gastos, 'importe');
      const multas_base_gastos = reduceImporteTotal(gastosMulta, 'importe');

      // descuentos de gastos
      const descuentos_gastos_totales_multas = reduceImporteTotal(filterDescuentosAplicados(gastosMulta));
      const descuentos_gastos_totales = reduceImporteTotal(filterDescuentosAplicados(gastos));

      // multas totales (considerando gastos y recargos)
      const multas_base = reduceImporteTotal(recargosMulta) + multas_base_gastos;

      // actualizaciones
      const omitirActualizaciones = crearOmisionDeActualizaciones(periodoCompletoSeleccionado, configCancelacionesRecargos);
      const [{ actualizaciones: rawActualizaciones }] = [cargo].map(omitirActualizaciones);
      const actualizaciones = rawActualizaciones.filter(filterNoMultas).map(defDescuentosNormalizer);
      const descuentos_actualizacion = reduceImporteTotal(filterDescuentosAplicados(actualizaciones));
      const actualizaciones_base = reduceImporteTotal(actualizaciones);

      // descuentos aplicables y especiales
      const descuentos_aplicables_total = reduceImporteTotal(descuentos_aplicables.filter(filterAplicado));
      const descuentos_especiales_total = reduceImporteTotal(descuentos_especiales.filter(filterAplicado));

      const descuentos_sobre_cargo = descuentos_aplicables_total + descuentos_especiales_total;

      const multa_gastos = reduceImporteTotal(gastosMulta, 'importe');
      const recargo_total_multas = reduceImporteTotal(recargosMulta);

      const multas_totales = (multa_gastos - descuentos_gastos_totales_multas)
        + (recargo_total_multas - descuentos_de_recargos_multas);

      const adeudo_total = ((cargo.importe - descuentos_sobre_cargo
        + (recargos_base - descuentos_de_recargos)
        + (actualizaciones_base - descuentos_actualizacion)
        + (gastos_totales - descuentos_gastos_totales)
        + multas_totales) * 100) / 100;

      const _descuentos_totales_junto_accesorios = descuentos_sobre_cargo + descuentos_de_recargos
        + descuentos_actualizacion + descuentos_gastos_totales + descuentos_gastos_totales_multas
        + descuentos_de_recargos_multas;

      return {
        ...cargo,
        // para descuentosRender
        descuentos_aplicables,
        descuentos_especiales,
        recargos,
        gastos,
        actualizaciones,
        recargosMulta,
        gastosMulta,
        // para TableAdeudo
        adeudo_total,
        actualizaciones_base,
        recargos_base,
        multas_base,
        gastos_totales,
        _descuentos_totales_junto_accesorios,
        // para GPM
        subtotal_gpm: adeudo_total - recargos_base - actualizaciones_base - multas_base,
      };
    });
  return _cargos;
};
